import * as React from 'react';
import { v4 as uuidV4 } from 'uuid';
import { ActivityIcon } from 'lucide-react';
import { useInvoiceStore } from 'pages/tracking-v2/invoice-details-page/hooks';
import { Timeline } from 'components/ui/timeline';
import { Card, CardContent } from 'components/ui/card';
import { Formatter } from 'utils';

import { UserCircleICon, TimelineContainer, TimelineSkeletonCard } from './ui-kit';

type TimeLineEvent = {
  id: string;
  createdAt: string;
  description: string;
  userName: string;
}

type ActivityItem = {
  activity: string
  createdAt: string
  [key: string]: any
}

type Props = React.ComponentProps<'div'>

export const ActivitiesTimeline = (props: Props) => {
  const { invoice } = useInvoiceStore();

  function getOccurrenceNumber(activity: string): string {
    const match = activity.match(/\((\d+)\)/)
    return match ? match[1] : activity
  }

  function removeRepeatedActivities(items: ActivityItem[]) {
    const map = new Map<string, ActivityItem>()

    for (const item of items) {
      const occurrenceNumber = getOccurrenceNumber(item.activity)
      const current = map.get(occurrenceNumber)

      if (!current) {
        map.set(occurrenceNumber, item)
        continue
      }

      const currentDate = new Date(current.createdAt).getTime()
      const itemDate = new Date(item.createdAt).getTime()

      if (itemDate > currentDate) {
        map.set(occurrenceNumber, item)
      }
    }

    return Array.from(map.values())
  }


  const timelineEvents = React.useMemo(() => {
    const timelineEvents: TimeLineEvent[] = [];
    
    const activities = removeRepeatedActivities(invoice?.activities || [])
   
    activities.forEach((activity) => {
      timelineEvents.push({
        id: uuidV4(),
        createdAt: activity.createdAt,
        description: activity.activity,
        userName: activity.user.name,
      });
    });

    invoice?.statuses?.forEach((status) => {
      timelineEvents.push({
        id: uuidV4(),
        createdAt: status.createdAt,
        description: `Mudança de status para ${status.status.name}`,
        userName: status?.user?.name || 'Sistema',
      });
    });

    return timelineEvents.sort((a, b) => {
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });
  }, [invoice]);

  if (!invoice) {
    return (
      <TimelineContainer
        {...props}
        timelineIcon={<ActivityIcon />}
        timelineTitle={'Atividades'}
      >
        <Timeline className={'py-6'}>
          {Array.from({ length: 3 }).map((_, index) => (
            <TimelineSkeletonCard key={index} />
          ))}
        </Timeline>
      </TimelineContainer>
    );
  }
  
  return (
    <TimelineContainer
      {...props}
      timelineIcon={<ActivityIcon />}
      timelineTitle={'Atividades'}
    >
      <Timeline className={'py-6 flex-1'}>
        {timelineEvents?.map((event) => (
          <Card key={event.id} className={'shadow-none w-full max-w-[460px]'}>
            <CardContent className={'pt-6'}>
              <div className={'flex items-center gap-2 mb-4'}>
                <UserCircleICon />
                <span className={'font-GilroySemibold text-[14px] text-black'}>{event.userName}</span>
              </div>
              <p className={'text-gray-600 text-xs'}>{event.description}</p>
              <p className={'text-gray-400 text-xs mt-4'}>
                {Formatter.date(event.createdAt, { format: 'dd/MM/yyyy HH:mm' })}
              </p>
            </CardContent>
          </Card>
        ))}
      </Timeline>
    </TimelineContainer>
  );
};
